import { ComponentEnum, ComponentI, LessonI } from "../../../types";
import { PasteWhere } from "../components/PasteComponentModal";
import { onCopyClipboard } from "./onCopyClipboard";

export type NotifyFunction = (
  type: "success" | "error" | "info",
  message: string,
) => void;

export interface SelectedComponentType {
  component: ComponentI | null;
  parentComponent: ComponentI | null;
}

export const handleCopyComponent = (
  selectedComponent: SelectedComponentType,
  notify: NotifyFunction,
) => {
  if (!selectedComponent.component) {
    notify("error", "No component to copy");
    return;
  }
  onCopyClipboard(JSON.stringify(selectedComponent.component));
  notify("success", "Component copied to clipboard!");
};

export const handlePastePreview = async (
  setPasteComponent: React.Dispatch<React.SetStateAction<ComponentI | null>>,
  setIsPasteComponentOpen: React.Dispatch<React.SetStateAction<boolean>>,
  notify: NotifyFunction,
) => {
  try {
    const clipboardText = await navigator.clipboard.readText();
    const component = JSON.parse(clipboardText) as ComponentI;
    if (!component.type || !component.content) {
      throw new Error("Invalid component format");
    }
    setPasteComponent(component);
    setIsPasteComponentOpen(true);
  } catch (error) {
    notify("error", "Please Copy a Component First");
  }
};

export const handleInsertComponent = (
  lessonState: LessonI,
  updateLessonState: React.Dispatch<React.SetStateAction<LessonI>>,
  currentPageId: string,
  newComponent1: ComponentI,
  selectedComponent?: SelectedComponentType,
  pasteWhere: PasteWhere = "End",
  setSelectedComponent?: React.Dispatch<
    React.SetStateAction<SelectedComponentType>
  >,
  handleFocusComponent?: (component: ComponentI | null) => void,
  notify?: NotifyFunction,
) => {
  const newComponent = {
    ...newComponent1,
    _id: `${newComponent1.type}_${Date.now()}`,
  };

  const currentPage = lessonState.pages.find(
    (page) => page._id === currentPageId,
  );
  const currentComponents = currentPage?.components || [];

  let insertIndex = 0;
  let parentComponent = null;

  const insertInsideLogicComponent = (
    where: PasteWhere,
    selectedComponent?: SelectedComponentType,
  ) => {
    parentComponent = selectedComponent?.parentComponent;
    const selectedIndex = currentComponents.findIndex(
      (comp) => comp._id === selectedComponent?.parentComponent?._id,
    );
    if (where === "Beginning") {
      currentComponents[selectedIndex].content.componentsList.unshift(
        newComponent,
      );
      insertIndex = -1;
    } else if (where === "End") {
      currentComponents[selectedIndex].content.componentsList.push(
        newComponent,
      );
      insertIndex = -1;
    }
  };

  const insertBelowSelectedComponent = () => {
    const selectedIndex = currentComponents.findIndex(
      (comp) => comp._id === selectedComponent.component?._id,
    );
    if (selectedIndex !== -1) {
      insertIndex = selectedIndex + 1;
    } else {
      if (selectedComponent.parentComponent?.content.componentsList) {
        if (
          newComponent.type === ComponentEnum.LOGIC_COMPONENT ||
          newComponent.type === ComponentEnum.FOR_EACH ||
          newComponent.type === ComponentEnum.TOGGLE
        ) {
          notify?.(
            "error",
            `${newComponent.type} cannot be added to ${selectedComponent.parentComponent?.type}`,
          );
          return;
        }
        const comp = currentComponents.findIndex(
          (c) => c._id === selectedComponent.parentComponent?._id,
        );
        const logicSelectedIndex = currentComponents[
          comp
        ].content.componentsList.findIndex(
          (c) => c._id === selectedComponent.component?._id,
        );
        if (logicSelectedIndex !== -1) {
          // Add new component to the parent component's componentsList
          parentComponent = currentComponents[comp];
          currentComponents[comp].content.componentsList.splice(
            logicSelectedIndex + 1,
            0,
            newComponent,
          );
          // Set insertIndex to -1 to skip adding to main components list
          insertIndex = -1;
        }
      }
    }
  };

  const insertInsideSelectedComponent = () => {
    parentComponent = selectedComponent?.component;
    const selectedIndex = currentComponents.findIndex(
      (comp) => comp._id === selectedComponent.component?._id,
    );
    if (
      selectedIndex !== -1 &&
      (selectedComponent.component?.type === ComponentEnum.LOGIC_COMPONENT ||
        selectedComponent.component?.type === ComponentEnum.FOR_EACH ||
        selectedComponent.component?.type === ComponentEnum.TOGGLE)
    ) {
      if (
        newComponent.type === ComponentEnum.LOGIC_COMPONENT ||
        newComponent.type === ComponentEnum.FOR_EACH ||
        newComponent.type === ComponentEnum.TOGGLE
      ) {
        notify?.(
          "error",
          `${newComponent.type} cannot be added to ${selectedComponent.component?.type}`,
        );
        insertIndex = -1;
        return;
      }
      currentComponents[selectedIndex].content.componentsList.unshift(
        newComponent,
      );
      insertIndex = -1;
    }
  };

  switch (pasteWhere) {
    case "Beginning":
      insertIndex = 0;
      if (
        selectedComponent.parentComponent?.type ===
        ComponentEnum.LOGIC_COMPONENT
      ) {
        insertInsideLogicComponent("Beginning", selectedComponent);
      }
      break;
    case "End":
      insertIndex = currentComponents.length;
      if (
        selectedComponent.parentComponent?.type ===
        ComponentEnum.LOGIC_COMPONENT
      ) {
        insertInsideLogicComponent("End", selectedComponent);
      }
      break;
    case "Below Selected Component":
      insertBelowSelectedComponent();
      break;
    case "Inside":
      insertInsideSelectedComponent();
      break;
    default: {
      const _exhaustiveCheck: never = pasteWhere;
      throw new Error(`Unhandled profile type: ${_exhaustiveCheck}`);
    }
  }

  if (insertIndex !== -1) {
    const newComponents = [
      ...currentComponents.slice(0, insertIndex),
      newComponent,
      ...currentComponents.slice(insertIndex),
    ];

    const newPages = lessonState.pages.map((page) => {
      if (page._id === currentPageId) {
        return { ...page, components: newComponents };
      }
      return page;
    });

    updateLessonState({ ...lessonState, pages: newPages });
  } else {
    const newPages = lessonState.pages.map((page) => {
      if (page._id === currentPageId) {
        return { ...page, components: currentComponents };
      }
      return page;
    });
    updateLessonState({ ...lessonState, pages: newPages });
  }

  if (setSelectedComponent) {
    setSelectedComponent({
      component: newComponent,
      parentComponent: parentComponent,
    });
  }
  if (handleFocusComponent) {
    window.setTimeout(() => {
      handleFocusComponent(newComponent);
    }, 500);
  }
};
